import { useState, useEffect } from 'react';
import { Download, Smartphone, Monitor } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { isIOS, isStandalone, getInstallInstructions } from '../lib/pwa';

export function PWAInstructions() {
  const [isInstalled, setIsInstalled] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'desktop'>('android');

  useEffect(() => {
    setIsInstalled(isStandalone());
    
    if (isIOS()) {
      setPlatform('ios');
    } else if (/Android/i.test(navigator.userAgent)) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }
  }, []);

  if (isInstalled) {
    return (
      <Card className="p-6 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
            <Download className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-green-900 dark:text-green-100">App Installed</h3>
            <p className="text-sm text-green-700 dark:text-green-300 mt-1">
              TENSION is installed on your device for quick access and offline support.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const instructions = getInstallInstructions();

  return (
    <Card className="p-6">
      <div className="space-y-4">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-neutral-900 dark:bg-white rounded-lg flex items-center justify-center">
            {platform === 'ios' || platform === 'android' ? (
              <Smartphone className="w-5 h-5 text-white dark:text-neutral-900" />
            ) : (
              <Monitor className="w-5 h-5 text-white dark:text-neutral-900" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="font-medium">Install TENSION</h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
              Add TENSION to your home screen for the best experience
            </p>
          </div>
        </div>

        <div className="space-y-3 pl-14">
          <div className="space-y-2">
            <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
              Installation steps:
            </p>
            <ol className="space-y-2 text-sm text-neutral-600 dark:text-neutral-400">
              {instructions.map((instruction, index) => (
                <li key={index} className="flex gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-xs font-medium">
                    {index + 1}
                  </span>
                  <span className="flex-1">{instruction}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="pt-2 space-y-2">
            <p className="text-xs font-medium text-neutral-900 dark:text-neutral-100">Benefits:</p>
            <ul className="space-y-1 text-xs text-neutral-600 dark:text-neutral-400">
              <li className="flex items-center gap-2">
                <div className="w-1 h-1 rounded-full bg-neutral-400" />
                <span>Quick access from your home screen</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1 h-1 rounded-full bg-neutral-400" />
                <span>Works offline after installation</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1 h-1 rounded-full bg-neutral-400" />
                <span>Native app-like experience</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1 h-1 rounded-full bg-neutral-400" />
                <span>Faster load times with caching</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </Card>
  );
}
